import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'
import { useInView } from 'react-intersection-observer'

import HowItWorks from 'components/Common/HowItWorks'
import NavigableOfferContent from 'components/OfferPage/NavigableOfferContent/NavigableOfferContent'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import {
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import { TOP_YMAL } from 'constants/fetchOfferStrategy'
import { fetchAlternativeOffers, fetchOfferById, fetchRelatedTravelItems, fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { offerProductDetailView } from 'actions/AnalyticsActions'
import { RouteComponentProps, withRouter } from 'react-router'
import Error404Page from 'components/Pages/Error404Page'
import Error500Page from 'components/Pages/Error500Page'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferContentLoader from 'components/OfferPage/OfferContentLoader'
import { BottomMobileRelatedTravelItems, TopMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import Heading from 'components/Luxkit/Typography/Heading'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import {
  getPropertyBedbankOffer,
  getPropertyAlternateOfferIds,
  getPropertyAlternateURL,
  getPropertyBestAlternateURL,
} from 'selectors/offerPageSelectors'
import { hasOrganicReferrer } from 'selectors/configSelectors'
import { scheduleIsCurrent } from 'lib/scheduleStatus'
import { isEnabledForFeature } from 'lib/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/highIntentOffers'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import { isOfferPageCuratedTripBannerEnabled } from 'selectors/featuresSelectors'
import useLoadOfferBasedLereRecommendations from 'components/Recommendations/YouMayAlsoLike/Offer/hooks/useLoadOfferBasedLereRecommendations'
import LastMinuteOfferContent from 'components/OfferPage/LastMinute/LastMinuteOfferContent'
import FlashHotelContent from './FlashHotel/FlashHotelContent'
import FlashTourContent from './FlashTour/FlashTourContent'
import AlwaysOnContent from './AlwaysOn/AlwaysOnContent'
import BundleAndSaveContent from './BundleAndSave/BundleAndSaveContent'
import UltraLuxContent from './UltraLux/UltraLuxContent'
import { fireEcommerceGTMEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import { EXPERIMENT, startExperiment } from 'lib/optimize'
import offerPageURL from 'lib/offerPageURL'
import OfferTripPromotion from './OfferTripPromotion'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useCuratedTrips } from 'tripPlanner/hooks/api'
import { BasicTrip } from 'tripPlanner/types/common'
import { EmptyArray } from 'lib/arrayUtils'
import { isBundleAndSave, isLEHotel, isTourV1Offer } from 'lib/offerTypes'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import * as Analytics from 'analytics/analytics'
import { accommodationOfferClickedEvent, viewOfferEvent } from 'analytics/optimizely/events'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import { useTranslation } from 'react-i18next'
import BusinessTravellerStickyBudgetBar from 'businessTraveller/components/budget/BusinessTravellerStickyBudgetBar'
import moment from 'moment'
import zIndex from 'styles/tools/z-index'

const StyledBusinessTravellerStickyBudgetBar = styled(BusinessTravellerStickyBudgetBar)`
  z-index: ${zIndex.floatingPanel}
`

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
  margin-top: ${rem(24)};
`

const StyledHowItWorks = styled(HowItWorks)`
  > * {
    margin-top: ${rem(32)};
    margin-left: ${rem(16)};
    margin-right: ${rem(16)};

    ${mediaQueryUp.md} {
      margin-left: ${rem(32)};
      margin-right: ${rem(32)};
    }
  }
`

const StyledPane = styled(Pane)`
  position: relative;
`

interface Props extends RouteComponentProps<{
  offerSlug: string;
  offerId: string;
}> {
  offer?: App.Offer;
  fetching: boolean;
  offerError?: any;
  traderInformation?: App.TraderInformation;
  regionCode: string;
  headlessMode: boolean;
  lereOfferStateAvailable: boolean;
  lereOfferCheckIn: string | undefined;
  lereOfferCheckOut: string | undefined;
  lereOfferDuration: number | undefined;
  propertyId?: string;
  propertyAlternateOfferIds?: App.AlternativeOffers;
  propertyBedbankOffer?: App.BedbankOffer;
  propertyAlternateUrl?: string;
  propertyBestAlternateUrl?: string;
  canDoSoldoutRedirect: boolean;
  relatedTravelItems: App.OfferState['relatedTravelItems'];
  propertyPageOfferId?: string;
  currency: string;
  isSpoofed: boolean;
  curatedTripBannerEnabled: boolean;
  utmMedium: string | undefined;
}

const TOUR_HOLIDAY_TYPES = [OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_CRUISE_SLUG]

function generateFilters(
  offerType: App.OfferType,
  holidayTypes: string[] = [],
  locations: string[] = [],
  strategyApplied = '',
) {
  const filters = {} as any
  if (holidayTypes && holidayTypes.length > 0) {
    filters.holidayTypes = holidayTypes
  }
  if (locations && locations.length > 0) {
    filters.locations = locations
  }
  if (strategyApplied) {
    filters.strategyApplied = strategyApplied
  }
  if (offerType === OFFER_TYPE_TOUR) {
    filters.holidayTypes = TOUR_HOLIDAY_TYPES
  }
  return filters
}

function OfferPage(props: Props) {
  const {
    offer,
    offerError,
    fetching,
    match,
    headlessMode,
    lereOfferStateAvailable,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferDuration,
    location: routerLocation,
    propertyId,
    propertyAlternateOfferIds,
    propertyBedbankOffer,
    propertyAlternateUrl,
    propertyBestAlternateUrl,
    canDoSoldoutRedirect,
    relatedTravelItems,
    regionCode,
    traderInformation,
    propertyPageOfferId,
    currency,
    isSpoofed,
    curatedTripBannerEnabled,
    utmMedium,
  } = props

  const [showNavOfferContent, setShowNavOfferContent] = useState(false)
  const dataLoaded = useRef(false)
  const dispatch = useAppDispatch()

  const { t } = useTranslation('common')

  useOptimizelyExperiment(OptimizelyClientSideExperiments.searchShowLoggedOutPrices)

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  // This one is used to track when the user actually sees the section
  const [navigable0Ref, isNavigableInView] = useInView({
    triggerOnce: true,
  })

  const setNavigableRef = useCallback(
    (node) => {
      navigableRef(node)
      navigable0Ref(node)
    },
    [navigable0Ref, navigableRef],
  )

  const offerTypeValidForLere = offer?.type && [
    OFFER_TYPE_HOTEL,
    OFFER_TYPE_ALWAYS_ON,
  ].includes(offer.type)

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      Analytics.track('offer-click-accommodation', accommodationOfferClickedEvent(offer))
      Analytics.track('view-offer', viewOfferEvent(offer))
      dataLoaded.current = true
    }
  }, [offer, currency])

  useEffect(() => {
    if (isNavigableInView && offer) {
      fireNonInteractionEvent({
        category: 'offer-page',
        action: 'navigable-impression',
        label: offer.analyticsType,
      })
    }
  }, [isNavigableInView, offer])

  useLoadOfferBasedLereRecommendations(
    offer?.id,
    !!offerTypeValidForLere && lereOfferStateAvailable,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferDuration,
  )

  useEffect(() => {
    if (!showNavOfferContent && routerLocation.hash) {
      setShowNavOfferContent(true)
    }
  }, [routerLocation.hash]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = routerLocation.hash
    }
  }, [showNavOfferContent]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (match.params.offerId) {
      dispatch(fetchOfferById(match.params.offerId))
    } else if (propertyPageOfferId) {
      dispatch(fetchOfferById(propertyPageOfferId))
    }
  }, [dispatch, match.params.offerId, propertyPageOfferId])

  useEffect(() => {
    if (offer && isEnabledForFeature(config.TRADER_INFORMATION_REGIONS, regionCode)) {
      dispatch(fetchTraderInformation(offer.id, regionCode))
    }
  }, [dispatch, offer?.id, regionCode]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offer) {
      dispatch(offerProductDetailView(offer))
      dispatch(fetchRelatedTravelItems(offer, t))
      global.dataLayer?.push({ offerType: offer.type })
    }

    if (utmMedium !== 'cpc') {
      startExperiment(EXPERIMENT.OFFER_PAGE_LOADED)
    }
  }, [offer?.id, utmMedium]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propertyId && !propertyAlternateOfferIds) {
      dispatch(fetchAlternativeOffers(propertyId, 'le'))
    } else if (!!propertyAlternateOfferIds?.bedbank?.length && !propertyBedbankOffer) {
      dispatch(fetchBedbankOfferById(propertyAlternateOfferIds.bedbank[0]))
    }
  }, [propertyId, propertyAlternateOfferIds, propertyBedbankOffer, dispatch])

  useEffect(() => {
    if (!!offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, dispatch])

  useEffect(() => {
    if (config.PAYMENTS_INSTALMENTS_EXPERIMENT_ID && offer?.type === OFFER_TYPE_HOTEL && !isSpoofed) {
      startExperiment(EXPERIMENT.PAY_IN_INSTALMENTS)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.type, isSpoofed])

  const onDatePickerClose = useCallback(() => {
    if (routerLocation.hash) {
      dispatch(replace({ ...routerLocation, hash: undefined }))
    }
  }, [dispatch, routerLocation])

  const recommendationFilters = useMemo(() =>
    (!offerError && !fetching && offer) ? generateFilters(
      offer.type,
      offer.holidayTypes,
      offer.locations,
      TOP_YMAL,
    ) : {}
  , [fetching, offer, offerError])

  const { data: trips = EmptyArray as BasicTrip[] } = useCuratedTrips({
    filters: { sourceItemCode: match.params.offerId },
    limit: 1,
  })
  const hasTrips = trips.length > 0
  useEffect(() => {
    if (hasTrips) {
      startExperiment(EXPERIMENT.CURATED_TRIP_OFFER_PAGE_PROMO_BANNER)
    }
  }, [hasTrips])

  if (offerError) {
    if (offerError.status === 404) {
      return <Error404Page />
    } else {
      return <Error500Page />
    }
  }

  if (fetching || !offer) {
    return <OfferContentLoader />
  }

  const isTourOffer = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2].includes(offer.parentType)
  const breadCrumbItems = relatedTravelItems[offer?.id] || []

  const {
    id,
    onlinePurchaseSchedule,
    type,
  } = offer

  const howItWorksItems = config.offerList.howItWorks[type]

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  let OfferPageContent

  if (isLEHotel(offer)) {
    if (offer.property.isUltraLux) {
      OfferPageContent = UltraLuxContent
    } else if (offer.type === OFFER_TYPE_LAST_MINUTE) {
      OfferPageContent = LastMinuteOfferContent
    } else if (offer.type === OFFER_TYPE_HOTEL) {
      OfferPageContent = FlashHotelContent
    } else if (offer.type === OFFER_TYPE_ALWAYS_ON) {
      OfferPageContent = AlwaysOnContent
    }
  } else if (isBundleAndSave(offer)) {
    OfferPageContent = BundleAndSaveContent
  } else if (isTourV1Offer(offer)) {
    OfferPageContent = FlashTourContent
  }

  if (offer.slug !== match.params.offerSlug) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  if (offer.type !== 'hotel' && propertyBestAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyBestAlternateUrl} />
  }

  if (!availableForPurchase && canDoSoldoutRedirect && propertyAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyAlternateUrl} />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageHeadData offer={offer} />
    <ErrorBoundary fallback={null}>
      <PromptCardSelector abandonedCart hotLead tripReengagement={false} stickyTop={60} />
    </ErrorBoundary>
    <StyledBusinessTravellerStickyBudgetBar
      checkInDate={moment(lereOfferCheckIn)}
      checkOutDate={moment(lereOfferCheckIn).add(lereOfferDuration, 'days')}
    />
    <OfferPageWrapper className="OfferPage">
      {!isTourOffer && !!breadCrumbItems.length && !headlessMode && <TopMobileRelatedTravelItems items={breadCrumbItems} />}
      <OfferPageContent
        offer={offer}
        key={offer.id}
        onFilterClose={onDatePickerClose}
      />
      <ObservableWrapper ref={setNavigableRef}>
        {curatedTripBannerEnabled && <TripGuard>
          <OfferTripPromotion offer={offer} />
        </TripGuard>}
        <StyledPane>
          {(navigableInView || showNavOfferContent) && (
            <>
              <NavigableOfferContent
                offerDetails={offer}
                traderInformation={traderInformation}
                hash={routerLocation.hash}
              />
              <PaneBody size="xl-y">
                {howItWorksItems && !offer.bundledWithFlightsOnly && !offer.disableBestPriceGuarantee && <LayoutContainer>
                  <Pane type="clean">
                    <PaneBody size="xl-y lg">
                      <Heading variant="heading3" align="center">
                        Why you should book with us
                      </Heading>
                      <StyledHowItWorks items={howItWorksItems} />
                    </PaneBody>
                  </Pane>
                </LayoutContainer>}
              </PaneBody>
            </>
          )}
        </StyledPane>
      </ObservableWrapper>
      <ErrorBoundary fallback={null}>
        {!isTourOffer &&
          <OfferRecommendations
            title={`Similar to ${offer.property?.name ?? 'this'}`}
            filters={recommendationFilters}
            id={id}
            trackingDescription="Offer LE"
            useGraph
          />
        }
      </ErrorBoundary>
      {!headlessMode &&
        <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} />
      }
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offerId = ownProps.propertyPageOfferId || ownProps.match?.params?.offerId || ''
  const offer = state.offer.offers[offerId] || state.offer.offerSummaries[offerId]
  const offerSharedState = state.recommendations.offerSharedState[offerId]
  return {
    regionCode: state.geo.currentRegionCode,
    canDoSoldoutRedirect: IS_SSR && hasOrganicReferrer(state),
    offer,
    traderInformation: state.offer.traderInformation[offerId]?.data,
    fetching: state.offer.offersLoading[offerId] && !offer,
    offerError: state.offer.offerErrors[offerId],
    headlessMode: state.config.headlessMode,
    propertyId: offer?.property?.id,
    propertyAlternateOfferIds: getPropertyAlternateOfferIds(state, offerId),
    propertyBedbankOffer: getPropertyBedbankOffer(state, offerId),
    propertyAlternateUrl: getPropertyAlternateURL(state, offerId),
    propertyBestAlternateUrl: IS_SSR && getPropertyBestAlternateURL(state, offerId),
    relatedTravelItems: state.offer.relatedTravelItems,
    lereOfferStateAvailable: !!offerSharedState,
    lereOfferCheckIn: offerSharedState?.checkIn,
    lereOfferCheckOut: offerSharedState?.checkOut,
    lereOfferDuration: offerSharedState?.duration,
    currency: state.geo.currentCurrency,
    isSpoofed: state.auth.account.isSpoofed,
    curatedTripBannerEnabled: isOfferPageCuratedTripBannerEnabled(state),
    utmMedium: state.utm.utmMedium,
  }
}

export default withRouter(connect(mapStateToProps)(OfferPage))
